import { createContext, useCallback, useMemo, useState, type ReactNode } from 'react';
import type { BootUser, User } from '@/types/api';
import { api } from '@/api';

export type AuthStatus = 'loading' | 'anon' | 'authenticated';

export interface AuthState {
  status: AuthStatus;
  user: User | null;
}

export interface AuthContextValue extends AuthState {
  /** Resolves to true on success; throws LoginError on failure. */
  login(username: string, password: string): Promise<void>;
  logout(): Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Read the user that the SPA shell injected into window.__USER__ (Phase 5),
 * if present. In Phase 1 mock dev this is null on first load, so the visitor
 * starts anonymous and the login page seeds it.
 */
function readBootUser(): User | null {
  if (typeof window === 'undefined') return null;
  const raw: BootUser | null | undefined = window.__USER__;
  if (!raw) return null;
  return {
    id: raw.id,
    username: raw.username,
    name: raw.name,
    role: raw.role,
    email: raw.email ?? undefined,
    orgId: raw.orgId ?? undefined,
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const user = readBootUser();
    return { status: user ? 'authenticated' : 'anon', user };
  });

  const login = useCallback(async (username: string, password: string) => {
    const res = await api.login(username, password);
    if (res.err !== '1' && res.err !== 1) {
      throw new LoginError(res);
    }
    if (!res.user) {
      throw new LoginError({ err: '-99', message: 'Login succeeded but no user was returned.' });
    }
    const user: User = {
      id: res.user.id,
      username: res.user.username,
      name: res.user.name,
      role: res.user.role,
      email: res.user.email ?? undefined,
      orgId: res.user.orgId ?? undefined,
    };
    // Mock dev: set state in memory and let the caller navigate client-side
    // (no PHP shell exists to inject window.__USER__ across a reload). When the
    // real backend + SPA shell land in Phase 5, switch this to a hard reload
    // (window.location.assign('/')) so the shell re-injects window.__USER__.
    setState({ status: 'authenticated', user });
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.logout();
    } finally {
      setState({ status: 'anon', user: null });
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, login, logout }),
    [state, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Typed login error so the page can show res.message. AST exit codes:
 *   -1 missing fields, -3 wrong credentials, -4 no user, 2 password reset required.
 */
export class LoginError extends Error {
  constructor(public payload: { err: string | number | unknown[]; message?: string }) {
    super(payload.message || (typeof payload.err === 'string' ? `Login failed (${payload.err})` : 'Login failed'));
  }
}
